import { STATE_BY_CODE, localHour, type StateRef } from './states.js';
import type { RegistryCoverage } from './dnc.js';

/**
 * The compliance gate. Every check here runs INSIDE the dial path — a record
 * that fails one is never placed in a queue, never offered to an agent and
 * never dialled. Nothing in this file is legal advice; the thresholds are the
 * agency's configured policy and must be reviewed by counsel.
 */

export interface ComplianceConfig {
  federalOpen: number;
  federalClose: number;
  /** Resolve federal against the tighter state rule. */
  stricterOfState: boolean;
  requireConsent: boolean;
  /** Maximum age of the last DNC scrub, in days. */
  scrubMaxAgeDays: number;
  /**
   * Attempts allowed in a rolling 7 days.
   *
   * Counted against the LEAD RECORD, not the telephone number. If the same
   * number appears on two lead rows that deduplication did not merge, each
   * carries its own count and the person can be called twice the cap. The
   * regulator counts calls to a number. Until this is counted per number,
   * treat the configured value as a ceiling per record and set it with that
   * in mind.
   */
  weeklyAttemptCap: number;
  retentionMonths: number;

  /* ---------------------------------------------------------------------
   * THE FOUR SETTINGS BELOW ARE STORED AND READ BY NOTHING. Each for its own
   * reason, and the reasons are not the same — which is the point of writing
   * them out one by one instead of grouping them under a single excuse.
   *
   * This block used to say they were unread "because the thing they govern —
   * the dialer — is not built", and that the settings screen labelled them as
   * pending. Both had stopped being true. The dialer is built and places real
   * calls through `TelnyxCallPort`; and the settings screen does not label
   * these as pending, because it does not render them at all — grep
   * apps/web/src for any of the four and nothing comes back. A comment that
   * explains an absence with a reason that has expired turns a known gap into
   * an unexplained one, which is how it stops being looked at.
   *
   * None of the four is a hole in what the platform does today, and three of
   * them cannot be, because the features they govern are switched off or do
   * not exist. Read each doc below for which.
   * ------------------------------------------------------------------- */

  /**
   * Abandonment ceiling for predictive dialing. TSR 16 CFR 310.4(b)(4): 3%.
   *
   * NOT PENDING — there is no predictive mode for it to govern. Nothing in
   * this product dials ahead of an agent: `dial_mode='predictive'` is
   * unrepresentable (migration 0026), every authorisation is single-use, and
   * the calling screen asks for the next one only after the last call ends.
   *
   * Said more carefully than the first version of this note, which claimed the
   * one-call-per-agent property was structural. It is not. `authorize()` does
   * not check for a live session belonging to the caller, so nothing in the
   * SERVICE stops an agent holding two authorisations and spending both; what
   * sequences them is the screen. The abandonment rate is zero because no
   * component dials without a waiting agent, not because the type system
   * forbids a second line.
   *
   * Kept as a stored field because agencies expect to see the number, and
   * because removing it would rewrite every stored settings row to no purpose.
   */
  abandonCap: number;
  /**
   * Announce that the call is recorded. Required outright in two-party-consent
   * states, where it is a criminal statute rather than a civil one.
   *
   * MOOT WHILE RECORDING IS OFF, and it is off: `app.ts` passes `record: false`
   * to the dialer unconditionally, so no call this platform places is
   * recorded and there is nothing to announce. What IS enforced, separately
   * and today, is the spoken disclosure — `disclosureFor()` resolves the
   * wording per state, the screen shows it, and `recordDisclosure` refuses to
   * log one without naming the version read. That is the mechanism; this flag
   * is not.
   *
   * Before recording is ever switched on, this has to become load-bearing and
   * the agency needs a written retention policy. Both, not either.
   */
  announceRecording: boolean;
  /**
   * Announce that the caller is automated. Several states now require it.
   *
   * NOT REACHED BECAUSE NO ARTIFICIAL VOICE CALLS. `LeadService.gate` states
   * `voice: 'human'` explicitly, and the gate refuses `artificial` outright in
   * every state not affirmatively marked `permitted` — which today is all of
   * them. The announcement question only arises once that refusal is lifted
   * for some state, and it has to be answered as part of lifting it.
   */
  announceAutomation: boolean;
  /**
   * Show a local-area caller ID. Prohibited or restricted in some states.
   *
   * NOT REACHED BECAUSE THE CALLER ID IS FIXED. Outbound calls present
   * `DIALER_CALLER_ID`, one number for the deployment, chosen by whoever
   * configures it. Nothing selects a number per lead, so there is no local
   * presence to permit or forbid.
   */
  localPresenceCallerId: boolean;
}

/**
 * Settings stored for an agency that nothing currently reads.
 *
 * Named so a settings screen or an export can say so out loud rather than
 * presenting a live-looking control that governs nothing. See the block above
 * for why each one is here: they are not four instances of the same pending
 * work, and three of the four cannot become live without another feature
 * existing first.
 */
export const PENDING_DIALER_SETTINGS: readonly (keyof ComplianceConfig)[] = [
  'abandonCap', 'announceRecording', 'announceAutomation', 'localPresenceCallerId',
];

export const DEFAULT_COMPLIANCE: ComplianceConfig = {
  federalOpen: 8,
  federalClose: 21,
  stricterOfState: true,
  requireConsent: true,
  scrubMaxAgeDays: 31,
  weeklyAttemptCap: 3,
  abandonCap: 0.03,
  retentionMonths: 60,
  announceRecording: true,
  announceAutomation: true,
  localPresenceCallerId: false,
};

export interface CallingWindow {
  open: number;
  close: number;
  /** The state rule that tightened the window, when one did. */
  stateRule: string | null;
  tz: string;
}

export function callingWindow(
  stateCode: string | null, cfg: ComplianceConfig = DEFAULT_COMPLIANCE,
): CallingWindow {
  const st: StateRef | undefined = stateCode ? STATE_BY_CODE.get(stateCode) : undefined;
  if (!st) {
    return { open: cfg.federalOpen, close: cfg.federalClose, stateRule: null, tz: 'America/New_York' };
  }
  return {
    open: cfg.stricterOfState ? Math.max(cfg.federalOpen, st.open) : cfg.federalOpen,
    close: cfg.stricterOfState ? Math.min(cfg.federalClose, st.close) : cfg.federalClose,
    stateRule: st.rule,
    tz: st.tz,
  };
}

export function isCallableNow(
  stateCode: string | null, at: Date = new Date(), cfg: ComplianceConfig = DEFAULT_COMPLIANCE,
): boolean {
  if (!stateCode) return false;
  const w = callingWindow(stateCode, cfg);
  const h = localHour(stateCode, at);
  return h >= w.open && h < w.close;
}

export function minutesUntilOpen(
  stateCode: string | null, at: Date = new Date(), cfg: ComplianceConfig = DEFAULT_COMPLIANCE,
): number {
  if (!stateCode) return 0;
  const w = callingWindow(stateCode, cfg);
  const h = localHour(stateCode, at);
  if (h >= w.open && h < w.close) return 0;
  const delta = h < w.open ? w.open - h : 24 - h + w.open;
  return Math.round(delta * 60);
}

/**
 * How long this person may still lawfully be called today, in minutes.
 *
 * WHY THIS IS WORTH A FUNCTION OF ITS OWN
 * A queue that ranks only by lead quality throws away calls it was allowed to
 * make. An agency working Michigan and Colorado from one room loses its
 * Michigan leads two hours before its Colorado ones, and an agent working
 * strictly best-first will reach the bottom of the Michigan list at 9:15pm
 * Eastern, when it is no longer legal to ring any of them. Nobody notices,
 * because nothing anywhere says "you had four hundred of these and the door
 * shut". This is the number that lets the queue notice.
 *
 * Zero means the window is shut — either it has closed for the day, or it has
 * not opened yet. Both are "you may not call this person this minute", and the
 * caller that needs to tell them apart already has `minutesUntilOpen`.
 */
export function minutesUntilClose(
  stateCode: string | null, at: Date = new Date(), cfg: ComplianceConfig = DEFAULT_COMPLIANCE,
): number {
  if (!stateCode) return 0;
  const w = callingWindow(stateCode, cfg);
  const h = localHour(stateCode, at);
  if (h < w.open || h >= w.close) return 0;
  return Math.round((w.close - h) * 60);
}

export type GateCode =
  | 'no_phone' | 'consent_missing' | 'consent_channel' | 'scrub_stale'
  | 'dnc_internal' | 'calling_window' | 'attempt_cap' | 'campaign_inactive'
  | 'licence' | 'lead_suppressed'
  /** The consumer withdrew consent. Blocks every voice, human included. */
  | 'consent_revoked'
  /** Artificial voice asked for, and the stored consent does not reach it. */
  | 'voice_not_permitted'
  /** The agency stopped all calling by hand. See `GateInput.dialingPaused`. */
  | 'dialing_paused';

export interface GateFailure { code: GateCode; check: string; message: string }
export interface GateWarning { code: string; check: string; message: string }

/**
 * Who is producing the sound the called party hears.
 *
 * This is the distinction the TCPA actually draws, and it is not the one most
 * people expect. It is not about autonomy, how human the system sounds, or how
 * closely a licensed person supervises. 47 U.S.C. 227(b) attaches to the AUDIO:
 * if a machine produces any sound the consumer hears, it is engaged; if every
 * sound comes from a live person in real time, it is not engaged at all.
 *
 * FCC 24-17 para. 5 puts it plainly — AI voices are artificial "because a
 * person is not speaking them." The corollary is the whole safe path: if a
 * person IS speaking them, there is no artificial voice.
 *
 * `'human'` therefore covers AI that dials, scores, transcribes, coaches an
 * agent on screen or writes the disposition — none of which the consumer
 * hears. `'artificial'` covers a synthesised or cloned voice, a recorded
 * greeting played before a human joins, and a prerecorded message — including
 * the one 47 CFR 64.1200(a)(7)(i) *requires* on an abandoned call, which is
 * why abandonment has to be engineered to zero rather than to three percent.
 *
 * A live agent monitoring an artificial voice and able to break in does NOT
 * make it human. The FCC denied exactly that petition in DA 20-1507 para. 12:
 * "the mere presence of a live operator ... does not negate the clear
 * statutory prohibition." FCC 24-17 para. 8 ratified it.
 */
export type VoiceMode = 'human' | 'artificial';

/** What a stored consent artifact permits, beyond merely existing. */
export interface ConsentScope {
  capturedAt: Date;
  scrubbedAt: Date | null;
  channels: string[];
  disclosureVersion: string | null;
  /** The disclosure expressly covered automated/prerecorded/artificial voice. */
  coversArtificialVoice?: boolean;
  /** The disclosure expressly covered wireless numbers. */
  coversWireless?: boolean;
  /** Who the consumer was told might call, as the disclosure worded it. */
  namedSellers?: string[];
  /** 'electronic' counts as written — 47 CFR 64.1200(f)(9) says so expressly. */
  signatureKind?: 'written' | 'electronic' | 'oral' | 'none' | null;
  /** When a withdrawal was RECEIVED. The ten-day clock runs from here. */
  revokedAt?: Date | null;
  /** How the withdrawal arrived, in the consumer's words where possible. */
  revokedMethod?: string | null;
}

/**
 * Does this list of named sellers literally include the calling agency?
 *
 * Deliberately crude: casefold, strip punctuation and the entity suffixes that
 * differ between how a disclosure writes a name and how a company registers it
 * ("Loyalty Life Agency LLC" vs "Loyalty Life"), then substring either way. A
 * false negative here produces a warning a person reads. A clever matcher that
 * produced a false POSITIVE would silently authorise an artificial voice on a
 * disclosure that never named anybody like this agency, which is the failure
 * worth engineering against.
 */
export function namesSeller(named: readonly string[], agency: string): boolean {
  const norm = (s: string) => s
    .toLowerCase()
    .replace(/\b(llc|l\.l\.c|inc|incorporated|corp|corporation|co|company|agency|group|ltd)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const a = norm(agency);
  if (!a) return false;
  return named.some((n) => {
    const x = norm(n);
    return x.length > 0 && (x.includes(a) || a.includes(x));
  });
}

export interface GateInput {
  phone: string | null;
  state: string | null;
  /** Consent artifact, or null when the lead has none. */
  consent: ConsentScope | null;
  /**
   * Which voice will be on the call. Defaults to `'human'`, because that is
   * the only mode that exists today and because defaulting to the permissive
   * value is how a gate quietly stops gating.
   */
  voice?: VoiceMode;
  /**
   * Wireless, landline, or nobody checked. Only narrows the artificial-voice
   * test: 227(b)(1)(A)(iii) is the mobile prong, and `'unknown'` is treated as
   * wireless because roughly half of US households have no landline and a
   * guess in the permissive direction is the expensive one.
   */
  lineType?: 'wireless' | 'landline' | 'unknown';
  /** The agency on whose behalf the call is placed, for the named-seller test. */
  agencyName?: string | null;
  /**
   * Whether a do-not-call registry covering THIS number has been downloaded,
   * and how recently. See `packages/core/src/dnc.ts`.
   *
   * Absent is treated as `'not_loaded'`, which fails a telemarketing call.
   * That is deliberate: a permissive default here is exactly what turned an
   * unchecked file green.
   */
  registryCoverage?: RegistryCoverage;
  /**
   * What kind of call this is, which decides which rules reach it.
   *
   * `'telemarketing'` — encouraging the purchase of something. 47 CFR
   * 64.1200(f)(12) turns on the PURPOSE of the call, not its outcome, so a
   * qualification call that transfers to a closer is telemarketing even though
   * it never quotes a price.
   *
   * `'servicing'` — a call about a policy this person already owns: a premium
   * about to bounce, a requirement holding up their case. Informational, not a
   * solicitation, and the do-not-call registry does not reach it.
   *
   * Defaults to `'telemarketing'`. The moment a servicing call adds "and while
   * I have you, let me review your coverage" it stops being one, so the
   * classification belongs to whoever built the campaign — not to the dialer,
   * and never to a default.
   */
  purpose?: 'telemarketing' | 'servicing';
  /**
   * When this number was last scrubbed against the do-not-call registry.
   *
   * THE CHECK no longer depends on the consent artifact — that is the fix, and
   * it is about control flow, not about where the date comes from. The scrub
   * check used to sit INSIDE `if (consent)`, so a lead with no consent record
   * was never scrub-checked at all, and turning `requireConsent` off silently
   * switched DNC-staleness enforcement off with it. A number nobody has
   * consent for is the *most* dangerous one to dial unscrubbed, not the least.
   * The check now runs for every number, and a missing date fails rather than
   * skipping it.
   *
   * THE VALUE may still come from the artifact. The gate falls back to
   * `consent.scrubbedAt` when this is null, for rows written before leads
   * carried their own column, and `compliance.scrub.test.ts` holds that
   * behaviour in place. This paragraph previously said callers "have to pass
   * it", which read as a promise that the gate never looks at the artifact —
   * and the line that does look is twenty lines further down. Both halves are
   * fine; the sentence describing them was not.
   */
  scrubbedAt: Date | null;
  onInternalDnc: boolean;
  /**
   * Attempts against THIS LEAD RECORD in the last rolling 7 days.
   *
   * Not against the number, though this said "this number" until somebody
   * checked. Both paths that feed it are keyed on the lead: the batch path
   * reads `attemptsByLead`, and the single-lead path — the one the dialer
   * takes — counts that lead's own `call` events. If one telephone number sits
   * on two lead rows that deduplication did not merge, each row carries its own
   * count and the household can be called twice the cap, while the regulator
   * counts calls to a number.
   *
   * That is the same caveat `weeklyAttemptCap` carries, and it belongs here
   * too, on the field the gate actually reads. A doc that quietly promises
   * per-number counting is how the caveat gets read once and then designed
   * around as though it had been fixed.
   */
  attemptsLast7Days: number;
  campaignActive: boolean;
  /** Set when a specific agent is being checked for eligibility. */
  agentLicensedInState?: boolean | null;
  suppressed: boolean;
  litigatorFlag?: boolean;
  channel?: 'voice' | 'sms';
  /**
   * The agency has stopped all outbound calling by hand.
   *
   * This is a kill switch, not a schedule. It exists because things go wrong in
   * ways no rule anticipates — a list turns out to be bad, a carrier complains,
   * a recording is discovered to be missing its disclosure — and somebody needs
   * to be able to stop every phone in the building in one action, without
   * waiting for anyone.
   *
   * It sits ON TOP of every other check here and replaces none of them. A human
   * throwing a switch cannot enforce a calling window across four time zones,
   * because the window is measured where the called party is, not where the
   * switch is. Both have to hold.
   */
  dialingPaused?: boolean;
}

export interface GateResult {
  ok: boolean;
  failures: GateFailure[];
  warnings: GateWarning[];
  window: CallingWindow;
  localHour: number | null;
  /**
   * Every check this call actually EVALUATED, by name, sorted.
   *
   * WHY THE GATE REPORTS THIS ITSELF
   * An authorisation carries this list as its evidence, and it used to be
   * rebuilt by a second function in the dialer that mirrored these branches
   * from memory. It had drifted: it never named the agency hold or the
   * registry-coverage check — the one the whole DNC registry exists for — and
   * it hid the scrub check behind `requireConsent`, although the scrub runs
   * for every number either way. A hand-maintained mirror of a branch table
   * stays correct only while somebody keeps noticing, and nobody notices. So
   * the branches report themselves and the mirror is gone.
   *
   * EVALUATED, NOT PASSED — read it together with `ok`. On a pass these are
   * the checks that passed, which is what the authorisation stores. On a
   * failure these are the checks that ran, some of them the ones in
   * `failures`. It never names a check that did not run: an evidence list that
   * overstates itself is worse than no list at all, because somebody will
   * produce it in a deposition.
   */
  checks: readonly string[];
}

/**
 * What may lawfully dial this record.
 *
 *   `none`       nothing — revoked, or no artifact at all
 *   `human_only` a live agent may call; a machine voice may not
 *   `ai_voice`   the stored consent reaches an artificial voice too
 *
 * Derived from the artifact every time rather than stored on the lead, for the
 * same reason `consents` exists at all: a boolean on a lead row records a
 * conclusion and loses the evidence, and the evidence is the thing a caller has
 * to produce. Change the artifact and the tier changes with it.
 *
 * `ai_voice` requires all four of:
 *   - the disclosure expressly covered artificial/automated voice
 *   - a written or electronic signature, not an oral yes
 *   - wireless covered, when the number is wireless or unchecked
 *   - somebody recorded who the disclosure named
 *
 * The last one is deliberately a test of whether the fact was RECORDED, not of
 * whether the named category covers this agency. "Do the words 'our licensed
 * insurance partners' include Loyalty Life Agency LLC?" is a question for a
 * lawyer reading the disclosure, and software that answered it by matching
 * strings would be inventing a legal opinion. So: refuse when nobody wrote it
 * down, and warn — see `complianceGate` — when what was written down does not
 * literally contain the agency's name.
 */
export function consentTier(
  consent: ConsentScope | null,
  opts: {
    lineType?: 'wireless' | 'landline' | 'unknown';
    /**
     * Where the called party is, which decides whether an artificial voice may
     * speak there AT ALL — separately from, and above, anything the consent
     * says.
     *
     * Omitting it means the tier cannot claim `ai_voice`, because a tier is
     * what a screen prints and what a queue filters on. A record labelled
     * AI-dialable that the gate then refuses is the precise drift this whole
     * function exists to prevent, and the reverse — labelled human-only when
     * the gate would have allowed it — costs an agency real calls. Neither is
     * acceptable, so no state means no claim.
     */
    state?: string | null;
  } = {},
): 'none' | 'human_only' | 'ai_voice' {
  if (!consent) return 'none';
  if (consent.revokedAt) return 'none';
  // The state answer cannot be argued away by a better consent artifact, so it
  // is asked first. Michigan prohibits soliciting by recorded or artificial
  // voice outright (MCL 445.111a) and every unresearched state is `unclear`,
  // which refuses on the same reasoning the do-not-call registry uses: a state
  // nobody looked up is not a state that came back clear.
  const st = opts.state ? STATE_BY_CODE.get(opts.state) : undefined;
  if ((st?.artificialVoice ?? 'unclear') !== 'permitted') return 'human_only';
  const wireless = (opts.lineType ?? 'unknown') !== 'landline';
  const signed = consent.signatureKind === 'written' || consent.signatureKind === 'electronic';
  const ok =
    consent.coversArtificialVoice === true &&
    signed &&
    (!wireless || consent.coversWireless === true) &&
    (consent.namedSellers?.length ?? 0) > 0;
  return ok ? 'ai_voice' : 'human_only';
}

/** Every reason this consent falls short of permitting an artificial voice. */
export function artificialVoiceShortfalls(
  consent: ConsentScope,
  lineType: 'wireless' | 'landline' | 'unknown' = 'unknown',
): string[] {
  const missing: string[] = [];
  if (consent.coversArtificialVoice !== true) {
    missing.push('the disclosure does not record that it covered automated or artificial-voice calls');
  }
  if (consent.signatureKind !== 'written' && consent.signatureKind !== 'electronic') {
    missing.push(
      consent.signatureKind === 'oral'
        ? 'the consent was oral, and an artificial voice needs a written or electronic signature'
        : 'no written or electronic signature is recorded',
    );
  }
  if (lineType !== 'landline' && consent.coversWireless !== true) {
    missing.push(
      lineType === 'unknown'
        ? 'the number has not been checked for line type and the disclosure does not record that it covered wireless'
        : 'the number is wireless and the disclosure does not record that it covered wireless',
    );
  }
  if ((consent.namedSellers?.length ?? 0) === 0) {
    missing.push('nobody recorded who the disclosure named as permitted to call');
  }
  return missing;
}

export function complianceGate(
  input: GateInput, at: Date = new Date(), cfg: ComplianceConfig = DEFAULT_COMPLIANCE,
): GateResult {
  const failures: GateFailure[] = [];
  const warnings: GateWarning[] = [];
  const channel = input.channel ?? 'voice';
  const voice: VoiceMode = input.voice ?? 'human';
  const window = callingWindow(input.state, cfg);
  const hour = input.state ? localHour(input.state, at) : null;
  /*
   * Named at the point the branch runs, never at the end from a second reading
   * of the same conditions. A list assembled separately is a second source of
   * truth about which checks happened, and this one is evidence.
   */
  const checks: string[] = [];
  const ran = (name: string): void => { checks.push(name); };

  // Checked first, and named first in the list, so the reason a floor of agents
  // suddenly cannot dial is the first thing any of them read.
  ran('agency_hold');
  if (input.dialingPaused) {
    failures.push({
      code: 'dialing_paused',
      check: 'Agency hold',
      message: 'All outbound calling is stopped for this agency. An owner or administrator can lift the hold.',
    });
  }
  ran('number');
  if (!input.phone) {
    failures.push({ code: 'no_phone', check: 'Number', message: 'No usable phone number on the record.' });
  }
  ran('suppression');
  if (input.suppressed) {
    failures.push({ code: 'lead_suppressed', check: 'Suppressed', message: 'This record is suppressed and cannot be contacted.' });
  }
  ran('internal_dnc');
  if (input.onInternalDnc) {
    failures.push({ code: 'dnc_internal', check: 'Internal DNC', message: 'This number is on the internal do-not-call list.' });
  }
  if (cfg.requireConsent) ran('consent');
  if (cfg.requireConsent && !input.consent) {
    failures.push({ code: 'consent_missing', check: 'Consent', message: 'No stored consent artifact for this number.' });
  }
  // Both of the next two run only when there is an artifact to read, and that
  // is independent of `requireConsent` — an agency that does not demand consent
  // still honours the one it was given.
  if (input.consent) ran('consent_channel');
  if (input.consent && !input.consent.channels.includes(channel)) {
    failures.push({
      code: 'consent_channel', check: 'Consent channel',
      message: `The stored consent covers ${input.consent.channels.join(', ') || 'nothing'} — not ${channel}.`,
    });
  }
  /*
   * Withdrawal outranks everything the artifact permits, and it is checked
   * whether or not `requireConsent` is on: a record whose consent was revoked
   * is the one record an agency most needs to stop calling, and switching the
   * consent requirement off must not switch that off with it.
   *
   * THIS BRANCH WAS DEAD CODE UNTIL SYNC 84, AND NOT FOR THE OBVIOUS REASON.
   * The `consents` table has carried `revoked_at` and `revoked_method` since
   * migration 0014, with two indexes built to read them. What was missing was
   * the three steps in between: the `Consent` type had no such fields, so the
   * row mapper dropped both off a `SELECT *`, and `LeadService.gate` built its
   * `ConsentScope` from four fields that did not include them. A withdrawal
   * written into that table was invisible here. All three are fixed; nothing
   * writes revocation yet, so no behaviour changed — what changed is that the
   * next thing to write one will be honoured.
   *
   * WHAT HONOURS A WITHDRAWAL IN PRACTICE IS STILL THE OTHER PATH, and it is
   * the better one: an agent marks `do_not_contact`, which writes an internal
   * DNC row AND suppresses the lead, and the gate refuses on both of those
   * above. That is the mechanism 47 CFR 64.1200(d) actually asks for — an
   * internal list, kept five years — and it works on numbers that never had a
   * consent artifact at all, which is most of them.
   *
   * So this is the second lock, not the lock. Said plainly because an auditor
   * who reads "we check for revoked consent" and stops there would be reading
   * the check that has never yet had anything to fire on.
   */
  if (input.consent) ran('consent_revocation');
  if (input.consent?.revokedAt) {
    failures.push({
      code: 'consent_revoked', check: 'Revoked',
      message: `Consent was withdrawn on ${input.consent.revokedAt.toISOString().slice(0, 10)}${
        input.consent.revokedMethod ? ` (${input.consent.revokedMethod})` : ''
      }. No voice may call this record.`,
    });
  }
  /*
   * An artificial voice needs its own permission, and needs it in writing.
   *
   * WHAT REACHES THIS BRANCH TODAY, STATED PLAINLY
   * Calls are real — `TelnyxCallPort` ships and dials — and every one of them
   * is placed by a licensed human. `LeadService.gate` states `voice: 'human'`
   * rather than leaving it to the `?? 'human'` default below, so nothing
   * reaches this branch. That is a fact about how the product is configured,
   * not a property of the code: the moment anything asks for `artificial`,
   * these refusals apply in full, and no consent artifact talks Michigan out
   * of MCL 445.111a.
   *
   * The comment here used to say the branch was unenforced "because nothing
   * places a call". That stopped being true when the carrier adapter shipped,
   * and a stale reason is how an unreached branch turns into an unnoticed one.
   */
  if (voice === 'artificial') ran('artificial_voice');
  if (voice === 'artificial') {
    /*
     * THE STATE ASKS FIRST, AND CONSENT CANNOT ANSWER FOR IT.
     *
     * Federal law says an artificial voice needs prior express written consent.
     * Several states say something else and stricter, and a state that
     * prohibits solicitation by recorded message does not care how good the
     * consent is — there is no consent exception to read.
     *
     * Michigan is the worked example and it is where this agency lives. MCL
     * 445.111a: "a person shall not make a telephone solicitation that consists
     * in whole or in part of a recorded message." The exemptions in 445.111e
     * are charitable solicitation, public safety solicitation and section 527
     * organisations — no insurance, no consent. So a perfect written consent
     * naming this exact agency still does not permit an artificial voice on a
     * Michigan number.
     *
     * `unclear` refuses too. Forty-nine of those rows have never been
     * researched, and the failure mode of guessing permissively is an
     * artificial voice on a phone in a state that bans it, at $500 to $1,500 a
     * call, discovered from a lawsuit. Whether a real-time synthesised voice is
     * even a "recorded message" is a genuinely open question — which is a
     * reason to get an opinion, not a reason to assume the answer.
     */
    const st = input.state ? STATE_BY_CODE.get(input.state) : undefined;
    const permission = st?.artificialVoice ?? 'unclear';
    if (permission !== 'permitted') {
      failures.push({
        code: 'voice_not_permitted',
        check: 'Artificial voice — state',
        message: permission === 'prohibited'
          ? `${st?.name ?? 'This state'} prohibits soliciting by recorded or artificial voice, and no amount of consent changes that. A live agent may still call.`
          : `Nobody has established that ${st?.name ?? 'this state'} permits an artificial voice to solicit. It stays refused until counsel says otherwise in writing. A live agent may still call.`,
      });
    }
    if (!input.consent) {
      failures.push({
        code: 'voice_not_permitted', check: 'Artificial voice',
        message: 'An artificial voice needs prior express written consent, and there is no consent artifact on this record. An existing business relationship does not supply it.',
      });
    } else if (!input.consent.revokedAt) {
      const missing = artificialVoiceShortfalls(input.consent, input.lineType ?? 'unknown');
      if (missing.length > 0) {
        failures.push({
          code: 'voice_not_permitted', check: 'Artificial voice',
          message: `This consent does not reach an artificial voice: ${missing.join('; ')}. A live agent may still call.`,
        });
      } else if (input.agencyName && !namesSeller(input.consent.namedSellers ?? [], input.agencyName)) {
        // Recorded, but not literally this agency. That may be perfectly good —
        // one-to-one consent was vacated in Insurance Marketing Coalition Ltd.
        // v. FCC (11th Cir. 2025), so a category is allowed — but whether this
        // category covers this agency is a reading of the disclosure, not a
        // string comparison, so it warns rather than refuses.
        warnings.push({
          code: 'seller_not_named', check: 'Named seller',
          message: `The disclosure named ${(input.consent.namedSellers ?? []).join(', ')}, which does not literally include ${input.agencyName}. A category may still cover it — have counsel read the wording before an artificial voice uses it.`,
        });
      }
    }
  }
  // Runs for every number, with or without a consent artifact.
  ran('scrub_freshness');
  const scrubbed = input.scrubbedAt ?? input.consent?.scrubbedAt ?? null;
  const ageDays = scrubbed ? (at.getTime() - scrubbed.getTime()) / 86_400_000 : Infinity;
  if (ageDays > cfg.scrubMaxAgeDays) {
    failures.push({
      code: 'scrub_stale', check: 'Scrub',
      message: scrubbed
        ? `Last DNC scrub was ${Math.round(ageDays)} days ago — over the ${cfg.scrubMaxAgeDays}-day ceiling.`
        : 'This number has never been scrubbed against the do-not-call registry.',
    });
  }
  /*
   * A scrub date is not a scrub.
   *
   * The date above says something ran. This says whether the thing it ran
   * against actually covers this number. Before the registry existed in the
   * product, `recordScrub` stamped every lead and checked only the agency's own
   * internal list — so importing 300,000 purchased numbers and pressing scrub
   * turned the whole file green without one of them being checked against the
   * federal registry.
   *
   * `undefined` means the caller did not resolve coverage, and that is not
   * treated as clearance either: it fails, for the same reason. A caller that
   * cannot say what was checked has not shown that anything was.
   *
   * SCOPED TO TELEMARKETING, because that is the scope of the rule. The
   * registry lives in 47 U.S.C. 227(c), which governs solicitation. A call to
   * an existing policyholder about a premium that is about to bounce is not a
   * solicitation, needs no registry check, and must not be blocked by one —
   * the reason queue exists to produce exactly those calls, and they are the
   * ones an agency can lawfully make on day one.
   *
   * The default is `'telemarketing'`, the strict side. A caller that does not
   * say what kind of call it is gets treated as a sales call, because that is
   * the assumption that fails safely.
   */
  const purpose = input.purpose ?? 'telemarketing';
  const coverage = input.registryCoverage ?? 'not_loaded';
  if (purpose === 'telemarketing') ran('registry_coverage');
  if (purpose === 'telemarketing' && coverage !== 'fresh') {
    failures.push({
      code: 'scrub_stale', check: 'Registry',
      message: coverage === 'stale'
        ? 'The do-not-call registry file covering this area code is older than the '
          + `${cfg.scrubMaxAgeDays}-day ceiling. Download it again before calling this number.`
        : 'No do-not-call registry has been loaded for this number\'s area code, so it has '
          + 'never actually been checked. A number that was never looked up is not a number '
          + 'that came back clear.',
    });
  }
  ran('calling_window');
  if (hour !== null && (hour < window.open || hour >= window.close)) {
    failures.push({
      code: 'calling_window', check: 'Calling window',
      message: `It is ${fmtHour(hour)} where they live. The permitted window is ${fmtHour(window.open)}–${fmtHour(window.close)}${window.stateRule ? ` (state rule: ${window.stateRule})` : ''}.`,
    });
  }
  if (!input.state) {
    failures.push({ code: 'calling_window', check: 'Calling window', message: 'No state on the record, so the calling window cannot be resolved.' });
  }
  ran('attempt_cap');
  if (input.attemptsLast7Days >= cfg.weeklyAttemptCap) {
    failures.push({
      code: 'attempt_cap', check: 'Attempt cap',
      message: `${input.attemptsLast7Days} attempts in the last 7 days — the cap is ${cfg.weeklyAttemptCap}.`,
    });
  }
  ran('campaign');
  if (!input.campaignActive) {
    failures.push({ code: 'campaign_inactive', check: 'Campaign', message: 'The campaign is not authorised for outbound right now.' });
  }
  /*
   * `null` and `undefined` mean nobody asked, and a licence nobody asked about
   * was not checked. Only an actual answer — true or false — counts as a check
   * having run, which is what keeps a system-placed call from carrying
   * "licence" in its evidence when no agent was involved.
   */
  if (input.agentLicensedInState === true || input.agentLicensedInState === false) ran('licence');
  if (input.agentLicensedInState === false) {
    failures.push({ code: 'licence', check: 'Licensing', message: 'This agent is not licensed in the lead\'s state, or the licence has expired.' });
  }
  // Evaluated on every call, so it is named on every call. It was the one
  // branch the first version of `checks` missed — for the ordinary reason, that
  // it produces a warning rather than a failure and so reads like scenery.
  ran('litigator');
  if (input.litigatorFlag) {
    warnings.push({ code: 'litigator', check: 'Litigator flag', message: 'This number appears on a known-litigator list. Supervisor approval advised.' });
  }
  return {
    ok: failures.length === 0,
    failures,
    warnings,
    window,
    localHour: hour,
    checks: checks.sort(),
  };
}

export function fmtHour(h: number): string {
  const H = Math.floor(h) % 24;
  const m = Math.round((h % 1) * 60);
  const ap = H < 12 ? 'am' : 'pm';
  const hh = H % 12 === 0 ? 12 : H % 12;
  return `${hh}${m ? `:${String(m).padStart(2, '0')}` : ''}${ap}`;
}
